"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.PublicCheckoutResponse = exports.PublicCheckoutInput = exports.PublicCheckoutSubscription = exports.PublicCheckoutBillingAddress = exports.PublicCheckoutOrder = exports.PublicCheckoutOrderPtloo = exports.PublicCheckoutOrderCommercial = exports.PublicCheckoutOrderRo = exports.PublicCheckoutServiceLine = void 0;
const typebox_1 = require("@sinclair/typebox");
const stored_payment_method_type_1 = require("../invoice/stored-payment-method-type");
const operations_1 = require("../operations/operations");
/**
 * Service-line discriminator on each Order inside the subscription. Today the public-checkout
 * backend only honors ROLL_OFF; the other variants are reserved for upcoming commercial /
 * portable-toilet checkout flows so the wire format doesn't need to change when they ship.
 * Values mirror SERVICE_LINE_KEYS in transport-types/operations/service-line-keys.ts.
 */
exports.PublicCheckoutServiceLine = typebox_1.Type.Union([
    typebox_1.Type.Literal('ROLL_OFF'),
    typebox_1.Type.Literal('COMMERCIAL'),
    typebox_1.Type.Literal('PT_LOO'),
]);
/**
 * Customer-controllable subset of an OrderRo. The rule for what belongs here is:
 * "the customer picks it on the checkout form". The three fields below correspond to:
 *   - requestedDeliveryDate: the delivery date picker
 *   - requestedSize / requestedType: the "Select Size" tile (e.g. 20 yd OPEN_TOP)
 *
 * Every OTHER field on model.OrderRo is deliberately omitted:
 *   - fileds that are sent through the signed-quote JWT
 *   - fields that are computed in the server side upon subscription creation
 */
exports.PublicCheckoutOrderRo = typebox_1.Type.Object({
    requestedDeliveryDate: typebox_1.Type.String(),
    requestedSize: typebox_1.Type.Number(),
    requestedType: typebox_1.Type.String(),
});
/**
 * Placeholder for the customer-controllable subset of an OrderCommercial. The actual field set will be filled in when commercial-checkout work
 * starts; same rule as PublicCheckoutOrderRo (customer-picked fields only, pricing comes
 * from the signed-quote JWT).
 */
exports.PublicCheckoutOrderCommercial = typebox_1.Type.Object({});
/**
 * Placeholder for the customer-controllable subset of an OrderPtloo. Same shape rationale
 * as PublicCheckoutOrderCommercial
 */
exports.PublicCheckoutOrderPtloo = typebox_1.Type.Object({});
/**
 * Customer-controllable subset of an Order. Polymorphic on `type`: today only the orderRo
 * sub-array is honored (and only when type=ROLL_OFF). The commercialOrder / orderPtloo
 * slots are reserved so callers can later send a commercial- or portable-toilet checkout
 * with the same wire shape — populate the matching sub-array and set `type` accordingly.
 *
 * The JSON field names (orderRo, commercialOrder, orderPtloo) mirror model.Order on the
 * backend so the request maps 1:1 into the domain type without renaming.
 */
exports.PublicCheckoutOrder = typebox_1.Type.Object({
    type: exports.PublicCheckoutServiceLine,
    // Bound directly as Operations.Location (the same shape backing model.Location on
    // operations-service). Server-controlled fields (id, haulerID, sortOrder, audit
    // timestamps/userIDs) are present as Type.Optional on Location — the client should
    // omit them; the backend stamps them after binding.
    serviceLocation: operations_1.Location,
    orderRo: typebox_1.Type.Optional(typebox_1.Type.Array(exports.PublicCheckoutOrderRo)),
    commercialOrder: typebox_1.Type.Optional(typebox_1.Type.Array(exports.PublicCheckoutOrderCommercial)),
    orderPtloo: typebox_1.Type.Optional(typebox_1.Type.Array(exports.PublicCheckoutOrderPtloo)),
});
/**
 * Billing address shape mirroring model.BillingAddress on the backend.
 * AddressLine1 is optional so the customer can fall back to "use service address as
 * billing" by omitting it.
 */
exports.PublicCheckoutBillingAddress = typebox_1.Type.Object({
    city: typebox_1.Type.String(),
    state: typebox_1.Type.String(),
    zip: typebox_1.Type.String(),
    country: typebox_1.Type.String(),
    addressLine1: typebox_1.Type.Optional(typebox_1.Type.String()),
    addressLine2: typebox_1.Type.Optional(typebox_1.Type.String()),
});
/**
 * Customer-controllable subset of a Subscription. The polymorphic Orders array is the
 * forward-compat hook: commercial / PT checkouts will reuse this endpoint by sending an
 * order with a different `type` and their respective sub-array populated.
 */
exports.PublicCheckoutSubscription = typebox_1.Type.Object({
    billingAddress: exports.PublicCheckoutBillingAddress,
    orders: typebox_1.Type.Array(exports.PublicCheckoutOrder),
});
/**
 * Input for creating a subscription through public checkout.
 * Payment-processor agnostic and used for the public checkout endpoint.
 */
exports.PublicCheckoutInput = typebox_1.Type.Object({
    // Customer info (new customer will be created)
    email: typebox_1.Type.String(),
    firstName: typebox_1.Type.String(),
    lastName: typebox_1.Type.String(),
    phoneNumber: typebox_1.Type.String(),
    company: typebox_1.Type.Optional(typebox_1.Type.String()),
    // Customer-facing notes (rendered as internal tickets after creation)
    sharedDeliveryNotes: typebox_1.Type.Optional(typebox_1.Type.String()),
    sharedPickupNotes: typebox_1.Type.Optional(typebox_1.Type.String()),
    // Payment authorization. Rainforest is the only processor wired up today — every hauler
    // routed to this endpoint pays via Rainforest, so paymentMethodID is a Rainforest token.
    // Re-add a stripe token here only alongside the server-side handling that would consume it.
    signedQuote: typebox_1.Type.String(),
    paymentMethodID: typebox_1.Type.Optional(typebox_1.Type.String()),
    // Identifies the physical card behind paymentMethodID. Each checkout tokenizes afresh, so
    // without it the customer accumulates one saved card per checkout for the same card.
    paymentMethodFingerprint: typebox_1.Type.Optional(typebox_1.Type.String()),
    // What kind of payment method paymentMethodID is, e.g., CARD, ACH.
    paymentMethodType: typebox_1.Type.Optional(stored_payment_method_type_1.StoredPaymentMethodType),
    // The subscription to create. See PublicCheckoutSubscription for the polymorphic shape.
    subscription: exports.PublicCheckoutSubscription,
});
/**
 * Response from successful public checkout
 */
exports.PublicCheckoutResponse = typebox_1.Type.Object({
    subscriptionID: typebox_1.Type.String(),
    orderID: typebox_1.Type.String(),
    customerID: typebox_1.Type.String(),
    message: typebox_1.Type.String(),
});
//# sourceMappingURL=public-checkout-input.js.map